import { CalendarClock, Check, Clock, Link2 } from "lucide-react";
import { Badge } from "@/components/ui/misc";
import { useTaskMutations } from "@/hooks/useWork";
import { fmtTime, minutesLabel, relativeDue } from "@/lib/dates";
import type { Task } from "@/lib/types";
import { cn } from "@/lib/utils";
import { PriorityDot } from "./PriorityBadge";
import { STATUS_LABELS } from "./TaskSheet";
import { t } from "@shared/i18n";

export function TaskRow({ task, onOpen, compact }: { task: Task; onOpen: (id: string) => void; compact?: boolean }) {
  const { update } = useTaskMutations();
  const done = task.status === "done";
  const due = relativeDue(task.dueAt);
  return (
    <div className={cn("group flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-surface", done && "opacity-60")}>
      <button
        aria-label={done ? t("Rouvrir la tâche") : t("Marquer comme terminée")}
        onClick={() => update.mutate({ id: task.id, status: done ? "todo" : "done" })}
        className={cn("mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors", done ? "border-success bg-success text-white" : "border-border-strong hover:border-brand")}
      >
        {done && <Check className="size-3" strokeWidth={3} />}
      </button>
      <button onClick={() => onOpen(task.id)} className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-2">
          <PriorityDot priority={task.priority} />
          <span className={cn("truncate text-[15px]", done && "line-through")}>{task.title}</span>
        </span>
        {!compact && (
          <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
            {due && <Badge tone={done ? "neutral" : due.tone}>{due.label}</Badge>}
            {task.scheduledStart && (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="size-3" /> {fmtTime(task.scheduledStart)}
              </span>
            )}
            {task.estimatedMinutes && (
              <span className="inline-flex items-center gap-1">
                <Clock className="size-3" /> {minutesLabel(task.estimatedMinutes)}
              </span>
            )}
            {task.projectName && (
              <span className="inline-flex items-center gap-1">
                <span className="size-1.5 rounded-full" style={{ background: task.projectColor ?? "#FFB347" }} /> {task.projectName}
              </span>
            )}
            {task.dependsOn.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <Link2 className="size-3" /> {task.dependsOn.length}
              </span>
            )}
            {task.status !== "todo" && !done && <span>{STATUS_LABELS[task.status]}</span>}
            {task.source === "coach" && <span className="text-primary">{t("Coach IA")}</span>}
          </span>
        )}
      </button>
    </div>
  );
}
